import { Component, signal, computed, effect, inject } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import {
  type UpdateUserConsentsRequest, type User, type UserConsentsResponse, type DealerConsentsDTO, type ConsentPartnerDTO, UserService, type ConsentChannel, type ConsentsDTO, type UserConsent, type UserConsentChannel
} from '@inside-hub-app/customer-portal-b2c-client'
import { Iframe, MatomoIntegrationService, PagePreviewDialogComponent, type PagePreviewDialogData, type PagePreviewDialogResponse, TransifexService, CustomerPortalSharedModule } from '@inside-hub-app/customer-portal-shared'
import { TranslationService } from '@emilfreydigital/transifex-angular'
import { B2CConfig } from '@inside-hub-app/customer-portal-config'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { NGXLogger } from 'ngx-logger'
import { DataService } from '../../../../services/data.service'
import { SharedService } from '../../../../services/shared.service'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { debounceTime, distinctUntilChanged } from 'rxjs'
import { filter, isEmpty, isEqual, orderBy, toLower } from 'lodash'
import { toSignal } from '@angular/core/rxjs-interop'

import { CommonModule } from '@angular/common'
import { RouterModule } from '@angular/router'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxGlideModule } from 'ngx-glide'
import { GoogleMapsModule } from '@angular/google-maps'
import { A11yModule } from '@angular/cdk/a11y'
import { TextFieldModule } from '@angular/cdk/text-field'
@Component({
  selector: 'customer-portal-app-revolution-gdpr',
  standalone: true,
  templateUrl: './gdpr.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule,
    NgxSkeletonLoaderModule,
    NgxGlideModule,
    GoogleMapsModule,
    A11yModule,
    TextFieldModule
  ]
})
export class gdprComponent {
  user = signal<User | null>(null)
  consentsResponse = signal<UserConsentsResponse | null>(null)
  disabled = signal<boolean>(false)
  updates = signal<ConsentsDTO[] | DealerConsentsDTO[]>([])
  locale = signal<string | null>(null)

  loadingConsents = signal<boolean>(false)
  loadingConsentDealers = signal<boolean>(false)

  consentDealers = signal<ConsentPartnerDTO[]>([])
  filteredConsentDealers = signal<ConsentPartnerDTO[]>([])

  dropdownPartnerIdFound = signal<boolean>(false)

  hasOnlyMarketingServicesGivenViaNewGdprOnDe = signal<boolean>(false)

  consentDealerControl = new FormControl()
  consentDealerValue = toSignal(this.consentDealerControl.valueChanges.pipe(
    debounceTime(500),
    distinctUntilChanged(isEqual)
  ), { initialValue: this.consentDealerControl.value })

  dropdownPartnerId = computed(() => this.consentDealerValue()?.partnerId ?? null)

  loading = computed(() => this.loadingConsents() || this.loadingConsentDealers())

  consentDealership = computed<DealerConsentsDTO | ConsentsDTO | null>(() => {
    const response = this.consentsResponse()
    if (isEmpty(response?.dealers)) return response?.group ?? null
    return response?.dealers?.[0] ?? null
  })

  consentsPartnerId = computed(() => this.consentDealership()?.partnerId)

  hasConsentDealers = computed(() => (this.consentDealers()?.length ?? 0) > 0)

  hasUnsignedDealershipConsent = computed(() => {
    const response = this.consentsResponse()
    return response.dealers?.some(el => el.consents?.some(c => !c.signed)) ?? false
  })

  hasUnsignedBrandsConsent = computed(() => {
    if (this.hasOnlyMarketingServicesGivenViaNewGdprOnDe()) {
      return false
    }
    const response = this.consentsResponse()
    return response.brands?.some(el => el.consents?.some(c => !c.signed)) ?? false
  })

  hasAnyBrandsConsent = computed(() => {
    if (this.hasOnlyMarketingServicesGivenViaNewGdprOnDe()) {
      return false
    }
    const response = this.consentsResponse()
    return response?.brands?.length > 0
  })

  sortedDealers = computed(() =>
    orderBy(this.consentsResponse()?.dealers ?? [], ['name'], ['asc'])
  )

  sortedBrands = computed(() =>
    orderBy(this.consentsResponse()?.brands ?? [], ['name'], ['asc'])
  )

  showConsents = computed(() => {
    if (this.hasOnlyMarketingServicesGivenViaNewGdprOnDe()) {
      return true
    } else {
      return this.hasConsentDealers() && this.dropdownPartnerIdFound() && !isEmpty(this.consentDealership())
    }
  })

  dealerChannelsValid = computed(() => {
    this.updates()
    return (this.consentsResponse()?.dealers ?? []).every((dealer: DealerConsentsDTO) =>
      (dealer.consents ?? []).every((consent: UserConsent) => {
        if (consent.channelType === 'allowed' && consent.checked === true) {
          return (consent.channels ?? []).some((channel: UserConsentChannel) => channel.allow === true)
        }
        return true
      })
    )
  })

  brandChannelsValid = computed(() => {
    this.updates()
    return (this.consentsResponse()?.brands ?? []).every((brand: ConsentsDTO) =>
      (brand.consents ?? []).every((consent: UserConsent) => {
        if (consent.channelType === 'allowed' && consent.checked === true) {
          return (consent.channels ?? []).some((channel: UserConsentChannel) => channel.allow === true)
        }
        return true
      })
    )
  })

  private readonly userService = inject(UserService)
  readonly dialog = inject(MatDialog)
  private readonly logger = inject(NGXLogger)
  private readonly transifexTranslationsService = inject(TranslationService)
  readonly transifexService = inject(TransifexService)
  readonly sharedService = inject(SharedService)
  private readonly data = inject(DataService)
  private readonly matomoIntegrationService = inject(MatomoIntegrationService)

  private readonly lastProcessedDealerValue = signal<string | ConsentPartnerDTO | null>(null)
  private readonly lastProcessedLocale = signal<string | null>(null)
  private readonly lastProcessedUserConsents = signal<UserConsentsResponse | null>(null)
  private readonly lastProcessedDealers = signal<ConsentPartnerDTO[] | null>(null)
  private readonly lastProcessedLoadingConsents = signal<boolean>(false)
  private readonly lastProcessedLoadingDealers = signal<boolean>(false)
  private readonly remoteConfigService = inject(EfRemoteConfigurationService<B2CConfig>)
  private readonly hasNewGDPR = this.remoteConfigService.get('hasNewGDPR')
  private readonly country = this.remoteConfigService.get('country.code')

  constructor () {
    effect(() => {
      const value = this.consentDealerValue()
      if (isEqual(value, this.lastProcessedDealerValue())) return

      if (typeof value === 'string') {
        this.filteredConsentDealers.set(this.filterDealers(value, this.consentDealers()))
      } else if (value?.partnerId && value.partnerId !== this.consentsPartnerId()) {
        this.data.getConsents(value.partnerId)
      }

      this.lastProcessedDealerValue.set(value)
    })

    const userSig = toSignal(this.data.onUserLoaded)
    effect(() => {
      const u = userSig()
      if (!u || isEqual(u, this.user())) return
      this.user.set(u)
    })

    const transifexLangSig = toSignal(this.transifexService.onTransifexLanguageChange)
    effect(() => {
      const locale = transifexLangSig()
      if (isEmpty(locale) || this.lastProcessedLocale() === locale) return
      this.locale.set(locale)
      this.data.getConsents(this.dropdownPartnerId())
      this.lastProcessedLocale.set(locale)
    })

    const consentsLoadedSig = toSignal(this.data.onUserConsentsLoaded)
    effect(() => {
      const consents = consentsLoadedSig()
      if (!consents || isEqual(consents, this.lastProcessedUserConsents())) return
      this.consentsResponse.set(this.sharedService.deepCopy(consents))
      this.updates.set([])
      this.disabled.set(false)
      this.setConsentDealerControl()
      this.lastProcessedUserConsents.set(consents)
    })

    const dealersLoadedSig = toSignal(this.data.onConsentDealersLoaded)
    effect(() => {
      const dealers = dealersLoadedSig()
      if (!dealers || isEqual(dealers, this.lastProcessedDealers())) return
      const copy = this.sharedService.deepCopy(dealers)
      this.consentDealers.set(copy)
      this.filteredConsentDealers.set(copy)
      this.setConsentDealerControl()
      this.lastProcessedDealers.set(dealers)
    })

    const loadingConsentsSig = toSignal(this.data.loading.consents)
    effect(() => {
      const loading = loadingConsentsSig() ?? false
      if (loading === this.lastProcessedLoadingConsents()) return
      this.loadingConsents.set(loading)
      this.lastProcessedLoadingConsents.set(loading)
    })

    const loadingDealersSig = toSignal(this.data.loading.consentDealers)
    effect(() => {
      const loading = loadingDealersSig() ?? false
      if (loading === this.lastProcessedLoadingDealers()) return
      this.loadingConsentDealers.set(loading)
      this.lastProcessedLoadingDealers.set(loading)
    })
  }

  setConsentDealerControl (): void {
    if (isEmpty(this.consentsResponse())) return

    if (this.hasConsentDealers()) {
      const consentDealer = this.consentDealers().find(d => d.partnerId === this.consentsPartnerId())
      if (isEmpty(consentDealer)) {
        this.dropdownPartnerIdFound.set(false)
      } else {
        this.dropdownPartnerIdFound.set(true)
        if (!isEqual(this.consentDealerControl.value, consentDealer)) {
          this.consentDealerControl.setValue(consentDealer)
        }
      }
    } else {
      this.checkWhetherOnlyMarketingServicesGivenViaNewGdprOnDe()
    }
  }

  /**
   * On Germany, it's possible to only give marketing services consents during registartion flow.
   * Therefore, in that case the GDPR2 integration should not be constrained by missing dropdown partner.
   * This method ensures consents are displayed regardless of missing main dealer consent, with excepton if any brand consents are given.
   */
  checkWhetherOnlyMarketingServicesGivenViaNewGdprOnDe (): void {
    if (!this.hasNewGDPR) {
      return
    }

    const consents = this.consentsResponse()
    const isGermany = this.country === 'de'
    const isMissingDealerButHasMarketingService = !this.hasConsentDealers() && consents.dealers.length === 1
    const dealerConsentsExistButConsentDealerDoesNot = isGermany && !isEmpty(consents) && isMissingDealerButHasMarketingService

    if (dealerConsentsExistButConsentDealerDoesNot) {
      this.hasOnlyMarketingServicesGivenViaNewGdprOnDe.set(true)
    } else {
      this.hasOnlyMarketingServicesGivenViaNewGdprOnDe.set(false)
    }
  }

  stripForbiddenConsentFields (obj): void {
    Object.keys(obj).forEach(key => {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        if (this.hasOnlyMarketingServicesGivenViaNewGdprOnDe() && key === 'brands') {
          delete obj.brands
        } else {
          this.stripForbiddenConsentFields(obj[key])
        }
      } else {
        if (key === 'legalText' || key === 'logo' || key === 'consentManagementUrl') {
          delete obj.legalText
          delete obj.logo
          delete obj.consentManagementUrl
        }
      }
    })
  }

  updateConsents (): void {
    this.matomoIntegrationService.trackEvent('My account', 'Data protection change')
    this.disabled.set(true)
    const response = this.consentsResponse()
    const newConsents: UserConsentsResponse = { group: response.group, dealers: response.dealers, brands: response.brands }
    const copy = this.sharedService.deepCopy(newConsents)
    this.stripForbiddenConsentFields(copy)
    const updateConsents: UpdateUserConsentsRequest = { url: window.location.href, userConsents: copy }
    this.userService.putConsents(updateConsents).subscribe(
      () => { this.consentsSaved() },
      error => {
        this.disabled.set(false)
        this.sharedService.openConfirmationPopup('shared.error', 'shared.general-error-message')
        this.logger.error(error)
      }
    )
  }

  consentsSaved (): void {
    this.disabled.set(false)
    this.updates.set([])
    this.sharedService.openConfirmationPopup('shared.success', 'customerPortal.customer-portal.details.change.consents-changed')
    this.data.getConsents(this.dropdownPartnerId())
  }

  downloadPrintFile (preview?: boolean): void {
    this.sharedService.showSnackbar('customerPortal.customer-portal.please-wait-loading-data', null, 0)
    this.userService.getConsentDocument(this.consentsPartnerId()).subscribe(
      response => {
        this.sharedService.closeSnackbar()
        const bytes = new Uint8Array(response)
        const file = new Blob([bytes], { type: 'application/pdf;base64' })
        const url = URL.createObjectURL(file)
        if (preview) this.openBlob(url)
        else {
          const a = document.createElement('a')
          document.body.appendChild(a)
          a.href = url
          a.download = (this.consentsResponse()?.document?.id ?? 'consents') + '.pdf'
          a.target = '_blank'
          a.click()
        }
      },
      () => { this.sharedService.showSnackbar('customerPortal.customer-portal.loading-data.error', null) }
    )
  }

  openBlob (url: string): void {
    const newWindow = window.open()
    if (newWindow) newWindow.location.href = url
    else this.sharedService.showSnackbar('customerPortal.customer-portal.allow-popups', null)
  }

  getConsentValue (consent, channel): boolean {
    if (channel === 'checked') return consent?.checked === true
    return consent?.channels?.find(s => s.name === channel)?.allow ?? false
  }

  hasPhone (): boolean {
    const phones = this.sharedService.findUserPhone(this.user())
    return this.user()?.isBusiness ? phones?.mobileBusiness != null : phones?.mobile != null
  }

  updateConsentValue (type: string, id: number, legalEntityId: number, channel: string, event): void {
    const response = this.consentsResponse()
    if (type === 'dealer') this.isValidConsents(id, legalEntityId, response.dealers, event, channel)
    else if (type === 'brand') this.isValidConsents(id, legalEntityId, response.brands, event, channel)
    else this.isValidConsents(id, legalEntityId, response.group, event, channel)
  }

  errorMessage (type: ConsentChannel): void {
    switch (type) {
      case 'telephone':
      case 'sms':
      case 'whatsapp':
        if (!this.hasPhone()) this.sharedService.openConfirmationPopup('shared.error', 'customerPortal.customer-portal.no-phone')
        break
      case 'post':
        if (!this.addressExist()) this.sharedService.openConfirmationPopup('shared.error', 'customerPortal.customer-portal.no-address')
        break
    }
  }

  addressExist (): boolean {
    return this.user()?.address != null
  }

  isValidConsents (id: number, legalEntityId: number, element, event, channel: string): void {
    const el = Array.isArray(element) ? element.find(d => d.id === id) : element
    const consentEl = el.consents.find(cd => cd.legalEntityId === legalEntityId)

    let consentValue
    if (channel === 'checked') {
      consentValue = consentEl
      consentValue.checked = this.sharedService.switchBoolean(consentValue.checked)
      if (!consentValue.checked && consentEl.channels) {
        consentEl.channels.forEach((ch: UserConsentChannel) => { ch.allow = false })
      }
    } else {
      consentValue = consentEl.channels.find(cd => cd.name === channel)
      consentValue.allow = event.checked
      consentValue.edit = this.sharedService.switchBoolean(consentValue.edit)
    }

    const currentUpdates = [...this.updates()]

    if (channel === 'checked' && !consentValue.checked && consentEl.channels) {
      consentEl.channels.forEach((ch: UserConsentChannel) => {
        const chIdx = currentUpdates.indexOf(ch)
        if (chIdx > -1) currentUpdates.splice(chIdx, 1)
      })
    }

    const index = currentUpdates.indexOf(consentValue)
    if (index > -1) currentUpdates.splice(index, 1)
    else currentUpdates.push(consentValue)
    this.updates.set(currentUpdates)
  }

  channelsNotValid (consent: UserConsent): boolean {
    if (consent.channelType !== 'allowed') return false
    return !(consent.channels ?? []).some((ch: UserConsentChannel) => ch.allow === true)
  }

  checkboxIsDisabled (consent, channel: string): boolean {
    return (
      consent.channels.find(s => s?.name === channel) == null ||
      (!this.hasPhone() && ['telephone', 'sms', 'whatsapp'].includes(channel)) ||
      (!this.addressExist() && channel === 'post')
    )
  }

  getTranslation (key: 'select-dealer'): string {
    if (key === 'select-dealer') return this.transifexTranslationsService.translate('Select dealer', { _key: 'customerPortal.customer-portal.gdpr.select-dealer', _tags: 'customer-portal, 3.21' })
    return ''
  }

  formatDealerName (dealer: ConsentPartnerDTO): string {
    if (isEmpty(dealer)) return ''
    const partnerName = [dealer.partnerName?.trim(), dealer.partnerSubname?.trim()].filter(Boolean).join(' ').trim()
    return [dealer.name?.trim(), partnerName].filter(Boolean).join(', ').trim()
  }

  filterDealers (value: string, array: ConsentPartnerDTO[]): ConsentPartnerDTO[] {
    const filterValue = !isEmpty(value) ? toLower(value.trim()) : null
    return filterValue != null
      ? this.sortDealers(filter(array, dealer => this.formatDealerName(dealer).toLowerCase().includes(filterValue)))
      : array
  }

  sortDealers (value: ConsentPartnerDTO[]): ConsentPartnerDTO[] {
    return orderBy(value, [dealer => dealer.name ?? '', dealer => dealer.partnerName ?? '', dealer => dealer.partnerSubname ?? ''], ['asc', 'asc', 'asc'])
  }

  getPrimaryLegalText (): string {
    let legalText = this.consentsResponse()?.legalText
    if (isEmpty(legalText)) legalText = this.transifexTranslationsService.translate('Privacy notice', { _key: 'customerPortal.customer-portal.privacy.privacynotice', _tags: 'customer-portal, 3.1' })
    return legalText
  }

  openConsentManagementPage (consent: ConsentsDTO): void {
    const data: PagePreviewDialogData = {
      url: consent?.consentManagementUrl ?? '',
      title: this.transifexTranslationsService.translate('Consent management page', {
        _key: 'customerPortal.customer-portal.gdpr.consent-management-page',
        _tags: 'customer-portal, 3.32'
      }),
      iframe: Iframe.ConsentManagement
    }

    const ref = this.dialog.open(PagePreviewDialogComponent, {
      panelClass: ['mat-dialog-cpt', 'scrollable', 'iframe-preview', 'no-lr-padding'],
      data
    })

    ref.afterClosed().subscribe((result: PagePreviewDialogResponse) => {
      if (result?.reloadData === true) this.consentsSaved()
    })
  }
}
